import { Link } from 'react-router-dom';
import { COMPANY, LEGAL_IS_DRAFT, LEGAL_UPDATED } from '../../lib/company';
import { rememberLegalLang, type LegalLang } from '../../lib/legalLang';

export const PORTUGUESE_PREVAILS = {
  en: 'The Portuguese version of this document is the authoritative one. This English version is a courtesy translation: if the two differ, the Portuguese version prevails.',
  pt: 'Em caso de divergência entre a versão portuguesa e a versão inglesa deste documento, prevalece a versão portuguesa.',
} as const;

/** Last-updated line, with the draft note while the text awaits legal review. */
export function UpdatedLine({ lang }: { lang: LegalLang }) {
  const updated = lang === 'pt' ? `Última atualização: ${LEGAL_UPDATED.pt}` : `Last updated: ${LEGAL_UPDATED.en}`;
  const draft = lang === 'pt' ? ' · Rascunho — a aguardar revisão jurídica' : ' · Draft — pending legal review';
  return <p className="text-xs text-app-muted mb-6">{updated}{LEGAL_IS_DRAFT ? draft : ''}</p>;
}

/** The company's legal details, as one block. */
export function CompanyDetails({ lang }: { lang: LegalLang }) {
  return (
    <address className="not-italic text-sm my-3 leading-relaxed">
      <strong>{COMPANY.name}</strong><br />
      {COMPANY.addressLines.map((line) => (<span key={line}>{line}<br /></span>))}
      {lang === 'pt' ? 'NIPC' : 'Company number (NIPC)'}: {COMPANY.nipc}<br />
      {lang === 'pt' ? 'Contacto' : 'Contact'}: <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
    </address>
  );
}

export function PrevailsNote({ lang }: { lang: LegalLang }) {
  return <p className="text-xs text-app-muted mt-8 italic">{PORTUGUESE_PREVAILS[lang]}</p>;
}

/** Portuguese / English switch, and links between the legal pages. */
export function LegalNav({ lang, onLang, current }: { lang: LegalLang; onLang: (l: LegalLang) => void; current: 'terms' | 'host-agreement' | 'privacy' }) {
  const pages: Array<{ key: typeof current; path: string; label: { pt: string; en: string } }> = [
    { key: 'terms', path: '/legal/terms', label: { pt: 'Termos de Serviço', en: 'Terms of Service' } },
    { key: 'privacy', path: '/legal/privacy', label: { pt: 'Política de Privacidade', en: 'Privacy Notice' } },
    { key: 'host-agreement', path: '/legal/host-agreement', label: { pt: 'Acordo de Anfitrião', en: 'Host Agreement' } },
  ];
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-6 text-sm">
      <nav aria-label={lang === 'pt' ? 'Documentos legais' : 'Legal documents'} className="flex flex-wrap gap-x-4 gap-y-1">
        {pages.map((p) => p.key === current
          ? <span key={p.key} className="text-app-fg font-medium">{p.label[lang]}</span>
          : <Link key={p.key} to={p.path} className="text-gold underline underline-offset-2">{p.label[lang]}</Link>)}
      </nav>
      <div role="group" aria-label={lang === 'pt' ? 'Idioma' : 'Language'} className="flex gap-1">
        {(['pt', 'en'] as const).map((l) => (
          <button
            key={l}
            type="button"
            aria-pressed={lang === l}
            onClick={() => { rememberLegalLang(l); onLang(l); }}
            className={`px-3 py-1 rounded-full border text-xs font-medium ${lang === l ? 'bg-gold text-ink border-gold' : 'border-app-border text-app-muted'}`}
          >
            {l === 'pt' ? 'Português' : 'English'}
          </button>
        ))}
      </div>
    </div>
  );
}
